import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { URL } from "../../utils/constants";
import { getUser } from "../../utils/api";
import styles from "./styles.module.css";
import { FaRegCopy, FaPlus, FaTrash, FaChevronDown, FaSave, FaRegCalendarAlt } from "react-icons/fa";

const PointOCKCard = () => {
  const { id } = useParams();

  const token = localStorage.getItem("auth_token");
  const authHeader = token ? { authorization: `Token ${token}` } : {};

  const [details, setDetails] = useState(null);
  const [canEdit, setCanEdit] = useState(false);

  // collapses
  const [openEdit, setOpenEdit] = useState(true);
  const [openUsers, setOpenUsers] = useState(false);
  const [openRefs, setOpenRefs] = useState(false);
  const [openAddObject, setOpenAddObject] = useState(false);
  const [openAddStage, setOpenAddStage] = useState(false);
  const [openAddStageType, setOpenAddStageType] = useState(false);

  // save state
  const [saving, setSaving] = useState(false);
  const [errText, setErrText] = useState("");
  const [okText, setOkText] = useState("");

  // справочники
  const [users, setUsers] = useState([]);
  const [objects, setObjects] = useState([]);
  const [stageTypes, setStageTypes] = useState([]); // [{id,name}]
  const [stages, setStages] = useState([]);         // [{id,stage_type,name,stage_type_detail}]
  const [selectedStageTypeId, setSelectedStageTypeId] = useState(""); // строка id для select
  const [qStages, setQStages] = useState("");
  
  // forms
  const [formNewObject, setFormNewObject] = useState({ name: "" });
  const [formNewStageType, setFormNewStageType] = useState({ name: "" });
  const [formNewStage, setFormNewStage] = useState({
    stage_type_id: "", // <-- выбранный тип для создания этапа
    name: "",
  });

  const [addingStageType, setAddingStageType] = useState(false);
  const [addStageTypeErr, setAddStageTypeErr] = useState("");
  const [addStageTypeOk, setAddStageTypeOk] = useState("");

  // ui state
  const [addingObject, setAddingObject] = useState(false);
  const [addingStage, setAddingStage] = useState(false);
  const [addObjErr, setAddObjErr] = useState("");
  const [addStageErr, setAddStageErr] = useState("");
  const [addObjOk, setAddObjOk] = useState("");
  const [addStageOk, setAddStageOk] = useState("");

  // документация для объектов ОСК
  const [selectedDocObjectId, setSelectedDocObjectId] = useState(""); // объект, к которому работаем с доками

  const [currentDocs, setCurrentDocs] = useState([]); // docs прикрепленные к объекту
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsErr, setDocsErr] = useState("");

  const [openDocsAttach, setOpenDocsAttach] = useState(false);
  const [openDocsAdd, setOpenDocsAdd] = useState(false);

  const [docsCatalog, setDocsCatalog] = useState([]); // общий справочник документов
  const [qDocs, setQDocs] = useState("");
  const [selDocs, setSelDocs] = useState(new Set());
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogErr, setCatalogErr] = useState("");

  const [formNewDoc, setFormNewDoc] = useState({ name: "", link: "" });

  const [docsOk, setDocsOk] = useState("");
  const [docsActionErr, setDocsActionErr] = useState("");

  // формат даты
  const datePickerRef = React.useRef(null);
  const [plannedRu, setPlannedRu] = useState("");

  // форма точки (редактируемые поля)
  const [form, setForm] = useState({
    address: "",
    description: "",
    status: "created",
    needs_shurf: false,
    is_visible: true,
    inventory_number: "",
    planned_finish_date: "",

    manager_id: "",         // одно значение
    executors_ids: new Set(), // мульти

    object_ref_id: "",      // одно значение
    objects_ock_ids: new Set(), // мульти

    main_stage_id: "",      // одно значение
    stages_ids: new Set(),  // мульти
  });

  // ───────── helpers ─────────
  const copyTextToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text ?? ""));
    } catch (err) {
      console.error("copy failed:", err);
    }
  };

  const normId = (v) => (v == null ? "" : String(v));
  const keyOf = (a) => String(a?.id ?? a?.archive_link ?? "");

  const formatOckStatusRu = (status) => {
    const map = { created: "Создана", in_work: "В работе", ready: "Готово", done: "Выполнена" };
    return map[status] || status || "—";
  };

  const toHref = (raw = "") => {
    const link = String(raw || "").trim();
    if (!link) return "#";
    if (/^https?:\/\//i.test(link)) return link;
    if (/^file:\/\//i.test(link)) return link;
    if (/^[a-zA-Z]:\\/.test(link)) return "file:///" + link.replace(/\\/g, "/");
    if (/^\\\\/.test(link)) {
      const noLeading = link.replace(/^\\\\/, "");
      return "file://" + noLeading.replace(/\\/g, "/");
    }
    return link;
  };

  const pad2 = (n) => String(n).padStart(2, "0");

  // ISO (YYYY-MM-DD) -> RU (DD.MM.YYYY)
  const isoToRu = (iso) => {
    const s = String(iso || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
    const [y, m, d] = s.split("-");
    return `${d}.${m}.${y}`;
  };

  // RU (DD.MM.YYYY) -> ISO (YYYY-MM-DD)  (возвращает "" если невалидно)
  const ruToIso = (ru) => {
    const s = String(ru || "").trim();
    if (!s) return "";
    const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (!m) return "";
    const dd = Number(m[1]);
    const mm = Number(m[2]);
    const yyyy = Number(m[3]);
    if (yyyy < 1900 || yyyy > 2100) return "";

    // проверка реальной даты
    const dt = new Date(yyyy, mm - 1, dd);
    if (dt.getFullYear() !== yyyy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return "";

    return `${yyyy}-${pad2(mm)}-${pad2(dd)}`;
  };

  // мягкая маска ввода DD.MM.YYYY (только цифры, сама ставит точки)
  const maskRuDate = (raw) => {
    const digits = String(raw || "").replace(/\D/g, "").slice(0, 8); // DDMMYYYY
    const d = digits.slice(0, 2);
    const m = digits.slice(2, 4);
    const y = digits.slice(4, 8);
    let out = d;
    if (digits.length > 2) out += "." + m;
    if (digits.length > 4) out += "." + y;
    return out;
  };

  // ───────── load permissions ─────────
  useEffect(() => {
    if (!token) return;
    getUser()
      .then((me) => {
        const groups = me?.groups || [];
        const ok =
          groups.some((g) => {
            const s = String(g).toLowerCase();
            return s === "ock" || s === "creator_ock";
          }) ||
          !!me?.is_staff ||
          !!me?.is_superuser;
        setCanEdit(!!ok);
      })
      .catch((e) => console.error("GET me error:", e));
  }, [token]);

  const isReadOnly = !canEdit;

  // ───────── load point ─────────
  useEffect(() => {
    if (!id) return;

    fetch(`${URL}/api/points-ock/${id}/`, { headers: authHeader })
      .then((r) => r.json())
      .then((data) => {
        setDetails(data);

        setForm((prev) => ({
          ...prev,
          address: data?.address || "",
          description: data?.description || "",
          status: data?.status || "created",
          needs_shurf: !!data?.needs_shurf,
          is_visible: data?.is_visible !== false,
          inventory_number: data?.inventory_number || "",
          planned_finish_date: data?.planned_finish_date || "",

          manager_id: normId(data?.manager),

          executors_ids: new Set((data?.executors || []).map((x) => String(x))),
          object_ref_id: normId(data?.object_ref),
          objects_ock_ids: new Set((data?.objects_ock || []).map((x) => String(x))),
          main_stage_id: normId(data?.main_stage),
          stages_ids: new Set((data?.stages || []).map((x) => String(x))),
          
        }));

        setPlannedRu(isoToRu(data?.planned_finish_date || ""));
      })
      .catch((e) => console.error("GET point error:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  // ───────── load справочников (1 раз) ─────────
  useEffect(() => {
    // users
    fetch(`${URL}/api/users/`, { headers: authHeader })
      .then((r) => r.json())
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch((e) => console.error("GET users error:", e));

    // objects
    fetch(`${URL}/api/ock-objects/`, { headers: authHeader })
      .then((r) => r.json())
      .then((data) => setObjects(Array.isArray(data) ? data : []))
      .catch((e) => console.error("GET objects error:", e));

    // stages
    fetch(`${URL}/api/points-ock-stage-types/`, { headers: authHeader })
      .then(r => r.json())
      .then(data => {
        const arr = Array.isArray(data) ? data : [];
        setStageTypes(arr);
        if (!selectedStageTypeId && arr.length) setSelectedStageTypeId(String(arr[0].id));
      });

    fetch(`${URL}/api/points-ock-stages/`, { headers: authHeader })
      .then(r => r.json())
      .then(data => setStages(Array.isArray(data) ? data : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ───────── objects & stages adding ─────────
  const createObject = async () => {
    if (!canEdit) return;
    if (!token) return alert("Нужна авторизация.");

    setAddObjErr("");
    setAddObjOk("");

    const name = String(formNewObject.name || "").trim();
    if (!name) {
      setAddObjErr("Укажите название объекта.");
      return;
    }

    setAddingObject(true);
    try {
      const r = await fetch(`${URL}/api/ock-objects/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ name }),
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${r.status}`);

      // добавили в справочник
      setObjects((prev) => [data, ...prev]);

      const newId = String(data?.id ?? "");
      if (newId) {
        // прикрепляем в objects_ock
        setForm((f) => {
          const next = new Set(f.objects_ock_ids);
          next.add(newId);
          // если основной объект пуст — ставим
          const object_ref_id = f.object_ref_id || newId;
          return { ...f, objects_ock_ids: next, object_ref_id };
        });
      }

      setQObjects("");
      setFormNewObject({ name: "" });
      setAddObjOk("Объект добавлен и прикреплён к точке");
      setOpenAddObject(false);
    } catch (e) {
      console.error(e);
      // часто приходит {"name":["ock object with this name already exists."]} или текст
      setAddObjErr(e?.message || "Не удалось создать объект");
    } finally {
      setAddingObject(false);
    }
  };

  const createStage = async () => {
    if (!canEdit) return;
    if (!token) return alert("Нужна авторизация.");

    setAddStageErr("");
    setAddStageOk("");

    const typeId = String(formNewStage.stage_type_id || "").trim();
    const name = String(formNewStage.name || "").trim();

    if (!typeId) return setAddStageErr("Укажите тип этапа.");
    if (!name) return setAddStageErr("Укажите название этапа.");

    setAddingStage(true);
    try {
      const r = await fetch(`${URL}/api/points-ock-stages/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ stage_type: Number(typeId), name }),
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${r.status}`);

      setStages((prev) => [data, ...prev]);

      const newId = String(data?.id ?? "");
      if (newId) {
        setForm((f) => {
          const next = new Set(f.stages_ids);
          next.add(newId);
          return { ...f, stages_ids: next };
        });
      }

      setFormNewStage((f) => ({ ...f, name: "" }));
      setAddStageOk("Этап добавлен");
      setOpenAddStage(false);
    } catch (e) {
      console.error(e);
      setAddStageErr(e?.message || "Не удалось создать этап");
    } finally {
      setAddingStage(false);
    }
  };

  const createStageType = async () => {
    if (!canEdit) return;
    if (!token) return alert("Нужна авторизация.");

    setAddStageTypeErr("");
    setAddStageTypeOk("");

    const name = String(formNewStageType.name || "").trim();
    if (!name) {
      setAddStageTypeErr("Укажите название типа этапа.");
      return;
    }

    setAddingStageType(true);
    try {
      const r = await fetch(`${URL}/api/points-ock-stage-types/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ name }),
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${r.status}`);

      // 1) добавляем в справочник типов
      setStageTypes((prev) => [data, ...prev]);

      // 2) выбираем только что созданный тип
      const newTypeId = String(data?.id ?? "");
      if (newTypeId) setSelectedStageTypeId(newTypeId);

      setFormNewStage((f) => ({ ...f, stage_type_id: newTypeId }));
      setFormNewStageType({ name: "" });
      setAddStageTypeOk("Тип этапа добавлен");
    } catch (e) {
      console.error(e);
      setAddStageTypeErr(e?.message || "Не удалось создать тип этапа");
    } finally {
      setAddingStageType(false);
    }
  };

  useEffect(() => {
  if (!openAddStage) return;
    setFormNewStage((f) => ({
      ...f,
      stage_type_id: f.stage_type_id || selectedStageTypeId || "",
    }));
  }, [openAddStage, selectedStageTypeId]);

  // ───────── docs managing ─────────
  const createDoc = async () => {
    if (!canEdit) return;
    if (!selectedDocObjectId) return setDocsActionErr("Выберите объект.");

    setDocsActionErr(""); setDocsOk("");

    const name = String(formNewDoc.name || "").trim();
    const link = String(formNewDoc.link || "").trim();

    if (!name || !link) return setDocsActionErr("Укажите название и ссылку.");

    try {
      const r = await fetch(`${URL}/api/ock-objects/${selectedDocObjectId}/create-doc/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ name, link }),
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${r.status}`);

      setCurrentDocs((prev) => [data, ...prev]);
      setFormNewDoc({ name: "", link: "" });
      setDocsOk("Документ добавлен");
      setOpenDocsAdd(false);
    } catch (e) {
      setDocsActionErr(e?.message || "Не удалось добавить документ");
    }
  };

  const toggleDocSelect = (docId) => {
    const idStr = String(docId);
    setSelDocs((prev) => {
      const next = new Set(prev);
      next.has(idStr) ? next.delete(idStr) : next.add(idStr);
      return next;
    });
  };

  const attachSelectedDocs = async () => {
    if (!canEdit) return;
    if (!selectedDocObjectId) return setDocsActionErr("Выберите объект.");

    const ids = Array.from(selDocs).map(Number).filter(Number.isFinite);
    if (!ids.length) return;

    setDocsActionErr(""); setDocsOk("");

    try {
      const r = await fetch(`${URL}/api/ock-objects/${selectedDocObjectId}/attach-docs/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ doc_ids: ids }),
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${r.status}`);

      // перезагрузим список прикрепленных (проще и надежнее)
      setSelDocs(new Set());
      setDocsOk("Документация прикреплена");
      setOpenDocsAttach(false);

      // refresh currentDocs:
      fetch(`${URL}/api/ock-objects/${selectedDocObjectId}/docs/`, { headers: authHeader })
        .then((rr) => rr.json())
        .then((d) => setCurrentDocs(Array.isArray(d) ? d : []))
        .catch(() => {});
    } catch (e) {
      setDocsActionErr(e?.message || "Не удалось прикрепить документацию");
    }
  };

  const detachDoc = async (docId) => {
    if (!canEdit) return;
    if (!selectedDocObjectId) return;

    if (!window.confirm("Открепить документ от объекта?")) return;

    setDocsActionErr(""); setDocsOk("");

    try {
      const r = await fetch(`${URL}/api/ock-objects/${selectedDocObjectId}/detach-docs/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ doc_ids: [Number(docId)] }),
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${r.status}`);

      setCurrentDocs((prev) => prev.filter((x) => x.id !== docId));
      setDocsOk("Откреплено");
    } catch (e) {
      setDocsActionErr(e?.message || "Не удалось открепить");
    }
  };

  // загрузка документации выбранного объекта
  useEffect(() => {
    if (!selectedDocObjectId) {
      setCurrentDocs([]);
      return;
    }

    const ctrl = new AbortController();
    setDocsLoading(true);
    setDocsErr("");

    fetch(`${URL}/api/ock-objects/${selectedDocObjectId}/docs/`, {
      headers: authHeader,
      signal: ctrl.signal,
    })
      .then((r) => r.json())
      .then((data) => setCurrentDocs(Array.isArray(data) ? data : []))
      .catch((e) => {
        if (e.name !== "AbortError") setDocsErr("Не удалось загрузить документацию объекта");
      })
      .finally(() => setDocsLoading(false));

    return () => ctrl.abort();
  }, [selectedDocObjectId, token]);

  // загрузка справочника документации для "прикрепить" (с debounce)
  useEffect(() => {
    if (!openDocsAttach) return;

    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      setCatalogLoading(true);
      setCatalogErr("");

      const q = qDocs ? `?q=${encodeURIComponent(qDocs)}` : "";
      fetch(`${URL}/api/ock-object-docs/${q}`, {
        headers: authHeader,
        signal: ctrl.signal,
      })
        .then((r) => r.json())
        .then((data) => setDocsCatalog(Array.isArray(data) ? data : []))
        .catch((e) => {
          if (e.name !== "AbortError") setCatalogErr("Не удалось загрузить список документации");
        })
        .finally(() => setCatalogLoading(false));
    }, 250);

    return () => {
      ctrl.abort();
      clearTimeout(timer);
    };
  }, [openDocsAttach, qDocs, token]);

  // ───────── save point ─────────
  const handleSave = async () => {
    if (!canEdit) return;
    if (!token) return alert("Нужна авторизация.");

    setErrText("");
    setOkText("");

    const payload = {
      address: String(form.address || "").trim(),
      description: String(form.description || "").trim(),
      status: form.status,
      needs_shurf: !!form.needs_shurf,
      is_visible: !!form.is_visible,
      inventory_number: String(form.inventory_number || "").trim(),
      planned_finish_date: form.planned_finish_date ? String(form.planned_finish_date).trim() : null,

      manager: form.manager_id ? Number(form.manager_id) : null,
      executors: Array.from(form.executors_ids).map(Number).filter(Number.isFinite),

      object_ref: form.object_ref_id ? Number(form.object_ref_id) : null,
      objects_ock: Array.from(form.objects_ock_ids).map(Number).filter(Number.isFinite),

      main_stage: form.main_stage_id ? Number(form.main_stage_id) : null,
      stages: Array.from(form.stages_ids).map(Number).filter(Number.isFinite),
    };

    // не заставляем отправлять null (чтобы не сбрасывать лишнее)
    Object.keys(payload).forEach((k) => payload[k] === null && delete payload[k]);

    setSaving(true);
    try {
      const res = await fetch(`${URL}/api/points-ock/${id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);

      setDetails(data);
      setOkText("Сохранено");
    } catch (e) {
      console.error(e);
      setErrText(e?.message || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  // ───────── фильтр пользователей/объектов/этапов (клиентский) ─────────
  const [qUsers, setQUsers] = useState("");
  const [qObjects, setQObjects] = useState("");

  const mainStageIdStr = String(form.main_stage_id || "");

  const filteredUsers = useMemo(() => {
    const q = qUsers.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const s = `${u.username} ${u.first_name || ""} ${u.last_name || ""} ${u.email || ""}`.toLowerCase();
      return s.includes(q);
    });
  }, [users, qUsers]);

  const filteredObjects = useMemo(() => {
    const q = qObjects.trim().toLowerCase();
    if (!q) return objects;
    return objects.filter((o) => String(o.name || "").toLowerCase().includes(q));
  }, [objects, qObjects]);

  const filteredStages = useMemo(() => {
    const typeId = String(selectedStageTypeId || "");
    const q = qStages.trim().toLowerCase();

    return stages
      .filter(s => String(s.stage_type) === typeId)
      .filter(s => {
        if (!q) return true;
        return String(s.name || "").toLowerCase().includes(q);
      });
  }, [stages, selectedStageTypeId, qStages]);

  const filteredStagesSide = useMemo(() => {
    const mainId = String(form.main_stage_id || "");
    return filteredStages.filter(s => String(s.id) !== mainId);
  }, [filteredStages, form.main_stage_id]);

  const filteredObjectsSide = useMemo(() => {
    const mainId = String(form.object_ref_id || "");
    return filteredObjects.filter((o) => String(o.id) !== mainId);
  }, [filteredObjects, form.object_ref_id]);

  if (!details) return <div className={styles.card}>Загрузка…</div>;

  return (
    <div className={styles.card}>
      <div className={styles.cardScroll}>
        <div className={styles.titleRow}>
            <div className={styles.title}>Точка ОСК #{details.id}</div>
            <button type="button" className={styles.iconBtn} title="Копировать координаты" onClick={() => copyTextToClipboard(`${details.lat}, ${details.long}`)}>
            <FaRegCopy />
            </button>
        </div>
        
        <div className={styles.panel}>
          <button type="button" className={styles.collapseHeader} onClick={() => setOpenEdit((v) => !v)} aria-expanded={openEdit}>
              <span className={styles.collapseTitle}>Редактирование</span>
              <FaChevronDown className={`${styles.chevron} ${openEdit ? styles.chevronOpen : ""}`} />
          </button>
          {openEdit && (
            <>
              <div className={styles.collapseBody}>
                <label className={styles.label}>
                    Адрес
                    <input className={styles.input} value={form.address} disabled={isReadOnly} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
                </label>
                <label className={styles.label}>
                    Описание
                    <textarea className={styles.textarea} value={form.description} disabled={isReadOnly} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                </label>
                <div className={styles.grid2}>
                    <label className={styles.label}>
                    Статус
                    <select className={styles.select} value={form.status} disabled={isReadOnly} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                        <option value="created">Создана</option>
                        <option value="in_work">В работе</option>
                        <option value="ready">Готово</option>
                        <option value="done">Выполнена</option>
                    </select>
                    </label>
                    <label className={styles.label}>
                      Плановая дата (дд.мм.гггг)
                      <div className={styles.dateRow}>
                        {/* Текстовый ввод с маской */}
                        <input
                          className={styles.input}
                          value={plannedRu}
                          disabled={isReadOnly}
                          onChange={(e) => {
                            const masked = maskRuDate(e.target.value);
                            setPlannedRu(masked);
                            const iso = ruToIso(masked);
                            setForm((f) => ({ ...f, planned_finish_date: iso || "" })); // в form держим ISO
                          }}
                          onBlur={() => {
                            // при выходе — если невалидно, оставим как ввёл, но ISO очистим
                            const iso = ruToIso(plannedRu);
                            if (!iso && plannedRu.trim()) {
                              setForm((f) => ({ ...f, planned_finish_date: "" }));
                            } else {
                              // нормализуем красивый вид (например 1.2.2026 не получится — маска держит 01.02.2026)
                              setPlannedRu(isoToRu(iso));
                            }
                          }}
                          placeholder="27.02.2026"
                          inputMode="numeric"
                        />
                        {/* Нативный date для календаря (можно скрыть визуально, но оставить кликабельным через кнопку) */}
                        <input
                          ref={datePickerRef}
                          type="date"
                          className={styles.dateNative}
                          value={form.planned_finish_date || ""}
                          disabled={isReadOnly}
                          onChange={(e) => {
                            const iso = e.target.value; // YYYY-MM-DD
                            setForm((f) => ({ ...f, planned_finish_date: iso }));
                            setPlannedRu(isoToRu(iso));
                          }}
                          aria-label="Выбор даты"
                        />
                        <button
                          type="button"
                          className={styles.iconBtn}
                          title="Выбрать дату"
                          disabled={isReadOnly}
                          onClick={() => {
                            const el = datePickerRef.current;
                            if (!el) return;
                            // Chrome/Edge: showPicker()
                            if (typeof el.showPicker === "function") el.showPicker();
                            else el.click(); // fallback
                          }}
                        >
                          <FaRegCalendarAlt />
                        </button>
                      </div>
                    </label>
                </div>
                <label className={styles.label}>
                    Инвентарный номер
                    <input
                      className={styles.input}
                      value={form.inventory_number}
                      disabled={isReadOnly}
                      onChange={(e) => setForm((f) => ({ ...f, inventory_number: e.target.value }))}
                    />
                </label>
                <div className={styles.switchRow}>
                    <div className={styles.switchItem}>
                      <div className={styles.switchText}>Требуется шурф</div>
                      <label className={styles.switch}>
                      <input
                          type="checkbox"
                          checked={!!form.needs_shurf}
                          disabled={isReadOnly}
                          onChange={(e) => setForm((f) => ({ ...f, needs_shurf: e.target.checked }))}
                      />
                      <span className={styles.slider} />
                      </label>
                    </div>
        
                    <div className={styles.switchItem}>
                      <div className={styles.switchText}>Видимая точка</div>
                      <label className={styles.switch}>
                      <input
                          type="checkbox"
                          checked={!!form.is_visible}
                          disabled={isReadOnly}
                          onChange={(e) => setForm((f) => ({ ...f, is_visible: e.target.checked }))}
                      />
                      <span className={styles.slider} />
                      </label>
                    </div>
                </div>
                {/* USERS */}
                <button type="button" className={styles.collapseHeaderInner} onClick={() => setOpenUsers((v) => !v)} aria-expanded={openUsers}>
                    <span className={styles.collapseTitle}>Пользователи (руководитель / исполнитель)</span>
                    <FaChevronDown className={`${styles.chevron} ${openUsers ? styles.chevronOpen : ""}`} />
                </button>
                {openUsers && (
                    <div className={styles.collapseBodyInner}>
                      <label className={styles.label}>
                          Руководитель
                          <select
                            className={styles.select}
                            value={form.manager_id}
                            disabled={isReadOnly}
                            onChange={(e) => setForm((f) => ({ ...f, manager_id: e.target.value }))}
                          >
                          <option value="">—</option>
                          {filteredUsers.map((u) => (
                              <option key={u.id} value={String(u.id)}>
                              {u.username} ({[u.last_name, u.first_name].filter(Boolean).join(" ").trim() || "—"})
                              </option>
                          ))}
                          </select>
                      </label>
                      <div className={styles.label}>Исполнители</div>
                      <div className={styles.pickerBox}>
                        <input
                          className={styles.pickerSearch}
                          value={qUsers}
                          onChange={(e) => setQUsers(e.target.value)}
                          placeholder="username / фамилия / email"
                        />
                        <ul className={styles.scrollList} role="listbox" aria-multiselectable="true" tabIndex={0}>
                            {filteredUsers.map((u) => {
                            const uid = String(u.id);
                            const selected = form.executors_ids.has(uid);
                            const title = `${u.username} ${u.last_name || ""} ${u.first_name || ""}`.trim();
                            return (
                                <li
                                key={`exec-${uid}`}
                                className={`${styles.listItem} ${selected ? styles.listItemSelected : ""}`}
                                disabled={isReadOnly}
                                onClick={() => {
                                    if (isReadOnly) return;
                                    setForm((f) => {
                                    const next = new Set(f.executors_ids);
                                    next.has(uid) ? next.delete(uid) : next.add(uid);
                                    return { ...f, executors_ids: next };
                                    })
                                }}
                                aria-disabled={isReadOnly}
                                aria-selected={selected}
                                title={title}
                                >
                                <span className={styles.listItemText}>{title}</span>
                                <span className={styles.checkMark} aria-hidden="true">
                                    {selected ? "✓" : ""}
                                </span>
                                </li>
                            );
                            })}
                        </ul>
                      </div>
                      <div className={styles.muted}>Выбрано: {form.executors_ids.size}</div>
                    </div>
                )}
                {/* REFS */}
                <button type="button" className={styles.collapseHeaderInner} onClick={() => setOpenRefs((v) => !v)} aria-expanded={openRefs}>
                    <span className={styles.collapseTitle}>Объекты / Этапы</span>
                    <FaChevronDown className={`${styles.chevron} ${openRefs ? styles.chevronOpen : ""}`} />
                </button>
                {openRefs && (
                    <div className={styles.collapseBodyInner}>
                      <label className={styles.label}>
                        Основной объект
                        <select
                          className={styles.select}
                          value={form.object_ref_id}
                          disabled={isReadOnly} 
                          onChange={(e) =>
                              setForm((f) => {
                                const id = e.target.value;
                                const next = new Set(f.objects_ock_ids);
                                if (id) next.delete(String(id)); // главный не может быть побочным
                                return { ...f, object_ref_id: id, objects_ock_ids: next };
                              })
                            }
                        >
                          <option value="">—</option>
                          {filteredObjects.map((o) => (
                              <option key={o.id} value={String(o.id)}>{o.name}</option>
                          ))}
                        </select>
                      </label>
                      <div className={styles.label}>Объекты</div>
                      <div className={styles.pickerBox}>
                        <input
                          className={styles.pickerSearch}
                          value={qObjects}
                          onChange={(e) => setQObjects(e.target.value)}
                          placeholder="Поиск по названию…"
                        />
                        <ul className={styles.scrollList} role="listbox" aria-multiselectable="true" tabIndex={0}>
                          {filteredObjectsSide.map((o) => {
                            const oid = String(o.id);
                            const selected = form.objects_ock_ids.has(oid);
                            return (
                              <li
                              key={`obj-${oid}`}
                              className={`${styles.listItem} ${selected ? styles.listItemSelected : ""}`}
                              disabled={isReadOnly}
                              onClick={() => {
                                  if (isReadOnly) return;
                                  setForm((f) => {
                                  const next = new Set(f.objects_ock_ids);
                                  next.has(oid) ? next.delete(oid) : next.add(oid);
                                  return { ...f, objects_ock_ids: next };
                                  })
                              }}
                              aria-disabled={isReadOnly}
                              aria-selected={selected}
                              title={o.name}
                              >
                              <span className={styles.listItemText}>{o.name}</span>
                              <span className={styles.checkMark} aria-hidden="true">{selected ? "✓" : ""}</span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                      <div className={styles.muted}>Выбрано объектов: {form.objects_ock_ids.size}</div>
                      <label className={styles.label}>
                        Главный этап
                        <select
                          className={styles.select}
                          value={form.main_stage_id}
                          disabled={isReadOnly}
                          onChange={(e) =>
                            setForm((f) => {
                              const id = e.target.value;
                              const next = new Set(f.stages_ids);
                              if (id) next.delete(String(id)); // главный не может быть побочным
                              return { ...f, main_stage_id: id, stages_ids: next };
                            })
                          }
                        >
                          <option value="">—</option>
                          {filteredStages.map(s => (
                            <option key={s.id} value={String(s.id)}>{s.name}</option>
                          ))}
                        </select>
                      </label>

                      <div className={styles.grid2}>
                        {/* LEFT: тип этапа + добавление типа */}
                        <div>
                          <label className={styles.label}>
                            Тип этапа
                            <select
                              className={styles.select}
                              value={selectedStageTypeId}
                              disabled={isReadOnly}
                              onChange={(e) => {
                                setSelectedStageTypeId(e.target.value);
                                setQStages("");
                              }}
                            >
                              <option value="">—</option>
                              {stageTypes.map((t) => (
                                <option key={t.id} value={String(t.id)}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          </label>

                          {canEdit && (
                            <>
                              <button
                                type="button"
                                className={styles.collapseHeaderInner}
                                onClick={() => setOpenAddStageType((v) => !v)}
                                aria-expanded={openAddStageType}
                              >
                                <span className={styles.collapseTitle}>
                                  <FaPlus style={{ marginRight: 6 }} />
                                  Добавить тип этапа
                                </span>
                                <FaChevronDown className={`${styles.chevron} ${openAddStageType ? styles.chevronOpen : ""}`} />
                              </button>

                              {openAddStageType && (
                                <div className={styles.collapseBodyInner}>
                                  <label className={styles.label}>
                                    Название типа
                                    <input
                                      className={styles.input}
                                      value={formNewStageType.name}
                                      onChange={(e) => setFormNewStageType({ name: e.target.value })}
                                      placeholder='Напр. "Подготовка", "Линия", "Согласования"'
                                    />
                                  </label>

                                  {addStageTypeErr && <div className={styles.error}>{addStageTypeErr}</div>}
                                  {addStageTypeOk && <div className={styles.success}>{addStageTypeOk}</div>}

                                  <button
                                    type="button"
                                    className={styles.primaryBtnWide}
                                    onClick={createStageType}
                                    disabled={addingStageType}
                                  >
                                    {addingStageType ? "Добавление..." : "Добавить тип"}
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* RIGHT: этапы + поиск */}
                        <div>
                          <div className={styles.label}>Этапы</div>
                          <div className={styles.pickerBox}>
                            <input
                              className={styles.pickerSearch}
                              value={qStages}
                              onChange={(e) => setQStages(e.target.value)}
                              placeholder="Поиск этапов…"
                              disabled={isReadOnly}
                            />
                            <ul className={styles.scrollList} role="listbox" aria-multiselectable="true" tabIndex={0}>
                              {filteredStagesSide.map((s) => {
                                const sid = String(s.id);
                                const selected = form.stages_ids.has(sid);
                                return (
                                  <li
                                    key={`stage-${sid}`}
                                    className={`${styles.listItem} ${selected ? styles.listItemSelected : ""}`}
                                    onClick={() => {
                                      if (isReadOnly) return;
                                      setForm((f) => {
                                        const next = new Set(f.stages_ids);
                                        next.has(sid) ? next.delete(sid) : next.add(sid);
                                        return { ...f, stages_ids: next };
                                      });
                                    }}
                                    aria-disabled={isReadOnly}
                                    aria-selected={selected}
                                    title={s.name}
                                  >
                                    <span className={styles.listItemText}>{s.name}</span>
                                    <span className={styles.checkMark} aria-hidden="true">
                                      {selected ? "✓" : ""}
                                    </span>
                                  </li>
                                );
                              })}
                              {!filteredStagesSide.length && <li className={styles.empty}>Нет этапов</li>}
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className={styles.muted}>Выбрано этапов: {form.stages_ids.size}</div>
                      {canEdit && (
                        <>
                          {/* ADD OBJECT */}
                          <button
                            type="button"
                            className={styles.collapseHeaderInner}
                            onClick={() => setOpenAddObject((v) => !v)}
                            aria-expanded={openAddObject}
                          >
                            <span className={styles.collapseTitle}>
                              <FaPlus style={{ marginRight: 6 }} />
                              Добавить новый объект
                            </span>
                            <FaChevronDown className={`${styles.chevron} ${openAddObject ? styles.chevronOpen : ""}`} />
                          </button>
                          {openAddObject && (
                            <div className={styles.collapseBodyInner}>
                              <label className={styles.label}>
                                Название объекта
                                <input
                                  className={styles.input}
                                  value={formNewObject.name}
                                  onChange={(e) => setFormNewObject({ name: e.target.value })}
                                  placeholder="Напр. станция метро «ЗИЛ»"
                                />
                              </label>
                              {addObjErr && <div className={styles.error}>{addObjErr}</div>}
                              {addObjOk && <div className={styles.success}>{addObjOk}</div>}
                              <button type="button" className={styles.primaryBtnWide} onClick={createObject} disabled={addingObject}>
                                {addingObject ? "Добавление..." : "Добавить объект"}
                              </button>
                            </div>
                          )}
                          {/* ADD STAGE */}
                          <button
                            type="button"
                            className={styles.collapseHeaderInner}
                            onClick={() => setOpenAddStage((v) => !v)}
                            aria-expanded={openAddStage}
                          >
                            <span className={styles.collapseTitle}>
                              <FaPlus style={{ marginRight: 6 }} />
                              Добавить новый этап
                            </span>
                            <FaChevronDown className={`${styles.chevron} ${openAddStage ? styles.chevronOpen : ""}`} />
                          </button>
                          {openAddStage && (
                            <div className={styles.collapseBodyInner}>
                              <label className={styles.label}>
                                Тип этапа
                                <select
                                  className={styles.select}
                                  value={formNewStage.stage_type_id}
                                  onChange={(e) => setFormNewStage((f) => ({ ...f, stage_type_id: e.target.value }))}
                                >
                                  <option value="">—</option>
                                  {stageTypes.map((t) => (
                                    <option key={t.id} value={String(t.id)}>{t.name}</option>
                                  ))}
                                </select>
                              </label>

                              <label className={styles.label}>
                                Название этапа
                                <input
                                  className={styles.input}
                                  value={formNewStage.name}
                                  onChange={(e) => setFormNewStage((f) => ({ ...f, name: e.target.value }))}
                                  placeholder='Напр. "Сбор исходных данных"'
                                />
                              </label>

                              {addStageErr && <div className={styles.error}>{addStageErr}</div>}
                              {addStageOk && <div className={styles.success}>{addStageOk}</div>}

                              <button type="button" className={styles.primaryBtnWide} onClick={createStage} disabled={addingStage}>
                                {addingStage ? "Добавление..." : "Добавить этап"}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                )}
                {errText && <div className={styles.error}>{errText}</div>}
                {okText && <div className={styles.success}>{okText}</div>}
                <button type="button" className={styles.primaryBtnWide} onClick={handleSave} disabled={saving}>
                    <FaSave style={{ marginRight: 6 }} />
                    {saving ? "Сохранение..." : "Сохранить"}
                </button>
              </div>

              <div className={styles.panel}>
                <h3 className={styles.h3}>Документация объектов ОСК</h3>

                <div className={styles.grid2}>
                  {/* LEFT: объект */}
                  <div>
                    <label className={styles.label}>
                      Объект
                      <select
                        className={styles.select}
                        value={selectedDocObjectId}
                        onChange={(e) => {
                          setSelectedDocObjectId(e.target.value);
                          setDocsOk("");
                          setDocsActionErr("");
                          setSelDocs(new Set());
                        }}
                      >
                        <option value="">—</option>
                        {objects.map((o) => (
                          <option key={o.id} value={String(o.id)}>
                            {o.name}
                          </option>
                        ))}
                      </select>
                    </label>

                    <div className={styles.muted}>
                      {docsLoading ? "Загрузка документации..." : docsErr ? docsErr : `Документов: ${currentDocs.length}`}
                    </div>
                  </div>

                  {/* RIGHT: кнопки */}
                  <div>
                    {canEdit && (
                      <>
                        <button
                          type="button"
                          className={styles.collapseHeaderInner}
                          onClick={() => setOpenDocsAttach((v) => !v)}
                          aria-expanded={openDocsAttach}
                          disabled={!selectedDocObjectId}
                          title={!selectedDocObjectId ? "Выберите объект" : "Прикрепить из существующих"}
                        >
                          <span className={styles.collapseTitle}>
                            <FaPlus style={{ marginRight: 6 }} />
                            Прикрепить из существующих
                          </span>
                          <FaChevronDown className={`${styles.chevron} ${openDocsAttach ? styles.chevronOpen : ""}`} />
                        </button>

                        <button
                          type="button"
                          className={styles.collapseHeaderInner}
                          onClick={() => setOpenDocsAdd((v) => !v)}
                          aria-expanded={openDocsAdd}
                          disabled={!selectedDocObjectId}
                          title={!selectedDocObjectId ? "Выберите объект" : "Добавить документ"}
                        >
                          <span className={styles.collapseTitle}>
                            <FaPlus style={{ marginRight: 6 }} />
                            Добавить документ
                          </span>
                          <FaChevronDown className={`${styles.chevron} ${openDocsAdd ? styles.chevronOpen : ""}`} />
                        </button>
                      </>
                    )}
                  </div>

                  {/* CURRENT DOCS (на всю ширину grid) */}
                  <div className={styles.fullRow}>
                    {currentDocs.map((d) => (
                      <div key={d.id} className={styles.linkRow}>
                        <a
                          href={toHref(d.link)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.link}
                          title={d.link}
                        >
                          {d.name}
                        </a>

                        <button
                          type="button"
                          className={styles.iconBtn}
                          title="Копировать ссылку"
                          onClick={() => copyTextToClipboard(d.link)}
                        >
                          <FaRegCopy />
                        </button>

                        {canEdit && (
                          <button
                            type="button"
                            className={styles.iconBtnDanger}
                            title="Открепить"
                            onClick={() => detachDoc(d.id)}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    ))}
                    {!currentDocs.length && !docsLoading && <div className={styles.empty}>Документация не прикреплена</div>}
                  </div>

                  {/* ATTACH PANEL (на всю ширину grid) */}
                  {openDocsAttach && canEdit && (
                    <div className={styles.fullRow}>
                      <div className={styles.collapseBodyInner}>
                        <div className={styles.label}>Прикрепить из существующих</div>

                        <div className={styles.pickerBox}>
                          <input
                            className={styles.pickerSearch}
                            placeholder="Поиск по названию или ссылке…"
                            value={qDocs}
                            onChange={(e) => setQDocs(e.target.value)}
                          />

                          <ul
                            className={`${styles.scrollList} ${styles.pickerList}`}
                            role="listbox"
                            aria-multiselectable="true"
                            tabIndex={0}
                          >
                            {catalogLoading ? (
                              <li className={styles.muted}>Загрузка…</li>
                            ) : catalogErr ? (
                              <li className={styles.error}>{catalogErr}</li>
                            ) : docsCatalog.length ? (
                              docsCatalog.map((d) => {
                                const idStr = String(d.id);
                                const selected = selDocs.has(idStr);
                                return (
                                  <li
                                    key={`doc-${idStr}`}
                                    className={`${styles.listItem} ${selected ? styles.listItemSelected : ""}`}
                                    onClick={() => toggleDocSelect(d.id)}
                                    aria-selected={selected}
                                    title={d.link}
                                  >
                                    <span className={styles.listItemText}>{d.name || "(без названия)"}</span>
                                    <span className={styles.checkMark} aria-hidden="true">
                                      {selected ? "✓" : ""}
                                    </span>
                                  </li>
                                );
                              })
                            ) : (
                              <li className={styles.empty}>Ничего не найдено</li>
                            )}
                          </ul>
                        </div>

                        {docsActionErr && <div className={styles.error}>{docsActionErr}</div>}
                        {docsOk && <div className={styles.success}>{docsOk}</div>}

                        <button
                          type="button"
                          className={styles.primaryBtn}
                          onClick={attachSelectedDocs}
                          disabled={!selDocs.size}
                          title={!selDocs.size ? "Выберите документы" : "Прикрепить выбранные"}
                        >
                          Прикрепить выбранные ({selDocs.size})
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ADD PANEL (на всю ширину grid) */}
                  {openDocsAdd && canEdit && (
                    <div className={styles.fullRow}>
                      <div className={styles.collapseBodyInner}>
                        <div className={styles.label}>Добавить документ</div>

                        <label className={styles.label}>
                          Название
                          <input
                            className={styles.input}
                            value={formNewDoc.name}
                            onChange={(e) => setFormNewDoc((f) => ({ ...f, name: e.target.value }))}
                            placeholder='Напр. "Том 4 — ПД"'
                          />
                        </label>

                        <label className={styles.label}>
                          Ссылка
                          <input
                            className={styles.input}
                            value={formNewDoc.link}
                            onChange={(e) => setFormNewDoc((f) => ({ ...f, link: e.target.value }))}
                            placeholder="T:\\... или https://..."
                          />
                        </label>

                        {docsActionErr && <div className={styles.error}>{docsActionErr}</div>}
                        {docsOk && <div className={styles.success}>{docsOk}</div>}

                        <button type="button" className={styles.primaryBtnWide} onClick={createDoc}>
                          Добавить
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PointOCKCard;